/**
 * Screenshot image utilities — the routing façade over {@link ./image-ops.js}.
 *
 * These five functions are `async` but their work is **pure-JS Jimp**: decode,
 * resize, JPEG encode and per-pixel scans all run on the calling thread, so they
 * block the event loop for their whole duration despite the Promise. Measured on
 * synthetic screenshots: 20–830 ms per call, scaling with image bytes, and
 * unbounded on `downsampleImage`, whose AI-filesystem caller reads arbitrary
 * on-disk images.
 *
 * Each function therefore resolves its options, builds a fully-specified
 * {@link ImageOpRequest}, and hands it to `runImageOpMaybeOffloaded` — which runs
 * it in a CPU-offload child (off the loop, under that child's wall-clock timeout
 * and RSS cap, drawing one shared expensive-gate slot) when
 * `browser.cpu-offload.images` is on, and inline otherwise. Routing lives HERE,
 * at the definition rather than at the ~13 call sites, so no caller can bypass it
 * and no call site had to change.
 *
 * Signatures and outputs are unchanged: with the knob off this file is
 * behaviourally identical to the pre-offload implementation.
 *
 * @module image-utils
 */

import { runImageOpMaybeOffloaded } from './cpu-offload/index.js';
import {
  ANNOTATION_BORDER_WIDTH,
  DEFAULT_BORDER_WIDTH,
  DEFAULT_GRID_SPACING,
  DEFAULT_HIGHLIGHT_COLOR,
  DEFAULT_MARGIN_SIZE,
  MAX_DIMENSION,
  type BoundingBox,
  type Dimensions,
  type ImageOpRequest,
  type IndexedAnnotation,
} from './image-ops.js';

export { MAX_DIMENSION };
export type { BoundingBox, IndexedAnnotation };

/** Route one fully-resolved operation and unwrap it to the base64 every caller returns. */
const runOp = async (request: ImageOpRequest): Promise<string> =>
  (await runImageOpMaybeOffloaded(request)).base64;

/**
 * Options for highlighting an element on a screenshot.
 */
export interface HighlightOptions {
  /** Border color as hex number (default: 0xFF0000FF - red) */
  color?: number;
  /** Border thickness in pixels (default: 3) */
  borderWidth?: number;
}

/**
 * Options for coordinate grid overlay.
 */
export interface CoordinateGridOptions {
  /** Grid line spacing in original viewport pixels (default: 100) */
  gridSpacing?: number;
  /** Margin size in pixels for coordinate labels (default: 32) */
  marginSize?: number;
}

/**
 * Downsample an image to fit within MAX_DIMENSION while preserving aspect ratio.
 * Returns a JPEG base64 string optimized for AI vision models.
 *
 * @param base64Data - Raw base64 image data (without data URL prefix)
 * @returns Downsampled JPEG as base64 string
 */
export async function downsampleImage(
  base64Data: string,
  maxDimension: number = MAX_DIMENSION
): Promise<string> {
  return runOp({ op: 'downsample', base64: base64Data, maxDimension });
}

/**
 * Crop a full-page screenshot to a bounding box region.
 * Used for coordinate-based dry-runs where locator.screenshot() isn't available.
 *
 * @param base64Data - Raw base64 image data (without data URL prefix)
 * @param box - Bounding box to crop to (in viewport coordinates)
 * @param viewportSize - Original viewport dimensions for coordinate mapping
 * @param padding - Extra pixels around the bounding box (default: 20)
 * @returns Cropped JPEG as base64 string
 */
export async function cropImage(
  base64Data: string,
  box: BoundingBox,
  viewportSize: Dimensions,
  padding: number = 20
): Promise<string> {
  return runOp({ op: 'crop', base64: base64Data, box, viewportSize, padding });
}

/**
 * Draw a highlight rectangle around an element on a screenshot.
 * The highlight is drawn as a colored border around the bounding box.
 *
 * @param base64Data - Raw base64 image data (without data URL prefix)
 * @param box - Bounding box of the element to highlight
 * @param options - Highlight styling options
 * @returns Modified image as base64 JPEG string
 */
export async function highlightElement(
  base64Data: string,
  box: BoundingBox,
  options?: HighlightOptions
): Promise<string> {
  return runOp({
    op: 'highlight',
    base64: base64Data,
    box,
    color: options?.color ?? DEFAULT_HIGHLIGHT_COLOR,
    borderWidth: options?.borderWidth ?? DEFAULT_BORDER_WIDTH,
  });
}

/**
 * Annotate a screenshot with indexed color-coded bounding boxes.
 * Each index (1-10) gets a unique color. The frontmatter contains the legend
 * mapping index→ref→element info.
 *
 * @param base64Data - Raw base64 image data (without data URL prefix)
 * @param annotations - Array of indexed annotations to draw
 * @param options - Optional settings including border width and original dimensions for scaling
 * @returns Annotated image as base64 JPEG string
 */
export async function annotateScreenshotIndexed(
  base64Data: string,
  annotations: IndexedAnnotation[],
  options?: {
    borderWidth?: number;
    /** Original viewport dimensions before downsampling. If provided, boxes are scaled to match the image. */
    originalDimensions?: Dimensions;
  }
): Promise<string> {
  return runOp({
    op: 'annotate',
    base64: base64Data,
    annotations,
    borderWidth: options?.borderWidth ?? ANNOTATION_BORDER_WIDTH,
    ...(options?.originalDimensions ? { originalDimensions: options.originalDimensions } : {}),
  });
}

/**
 * Add a coordinate grid overlay to a screenshot.
 *
 * Creates a new image with margins for coordinate labels.
 * Grid lines are drawn at specified intervals in ORIGINAL viewport coordinates,
 * but scaled to match the downsampled image dimensions.
 * Labels show original viewport coordinates so clicking coordinates match.
 *
 * @param base64Data - Raw base64 image data (without data URL prefix) - already downsampled
 * @param originalDimensions - Original viewport dimensions before downsampling
 * @param options - Grid styling options
 * @returns Image with coordinate grid as base64 JPEG string
 */
export async function addCoordinateGridOverlay(
  base64Data: string,
  originalDimensions: Dimensions,
  options?: CoordinateGridOptions
): Promise<string> {
  return runOp({
    op: 'grid',
    base64: base64Data,
    originalDimensions,
    gridSpacing: options?.gridSpacing ?? DEFAULT_GRID_SPACING,
    marginSize: options?.marginSize ?? DEFAULT_MARGIN_SIZE,
  });
}
